import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  OutlinedInput,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  Select,
  MenuItem,
} from '@mui/material'
import useAccounts from 'hooks/models/useAccounts'
import usePortfolios from 'hooks/models/usePortfolios'
import { useCallback, useMemo, useState } from 'react'
import AccountsByProvider from './AccountsByProvider'
import PortfoliosSelect from './PortfoliosSelect'
import Label from 'components/Label'
import { LoadingButton } from '@mui/lab'
import { cloneDeep } from 'lodash'
import useSnackbar from 'hooks/useSnackbar'
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { SearchOutlined } from '@mui/icons-material'

const createStyles = (theme) => ({
  container: {
    height: '100%',
    padding: theme.spacing(1.5),
    paddingBottom: 0,
    backgroundColor: theme.palette.secondary.lighter,
    borderLeft: `1px solid ${theme.palette.secondary.dark}`,
  },
  form: {
    height: 'calc(100% - 80px)',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.secondary.light,
    borderRadius: '4px',
    border: `1px solid ${theme.palette.secondary.dark}`,
  },
  buttonContainer: {
    borderTop: `1px solid ${theme.palette.secondary.darker}`,
    padding: '0.75rem 0.5rem',
    backgroundColor: theme.palette.secondary.light,
    marginLeft: '-0.75rem',
    marginRight: '-0.75rem',
    width: 'calc(100% + 1.5rem)',
    flexBasis: 'auto !important',
    maxWidth: 'calc(100% + 1.5rem) !important',
  },
  button: {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.secondary.darker,
    },
  },
  titleContainer: {
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  mappedContainer: {
    borderTop: `1px solid ${theme.palette.secondary.dark}`,
    paddingTop: theme.spacing(1),
    maxHeight: 'calc(100vh - 365px)',
    overflow: 'hidden',
    height: '100%',
  },
  errorListBox: {
    backgroundColor: theme.palette.grey[300],
    py: 1,
    px: 2,
  },
  errorBackButton: {
    width: 'fit-content',
  },
  search: {
    boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
    borderRadius: '20px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
      borderRadius: '20px',
    },
  },
  itemsContainer: {
    height: '100%',
    overflow: 'auto',
  },
  portfolioItem: {
    borderRadius: '4px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    padding: `0.5rem 1rem`,
    backgroundColor: theme.palette.secondary.main,
    height: 'fit-content',
    flexBasis: 'auto !important',
    '& .MuiCheckbox-root ': {
      padding: 0,
      margin: 0,
      marginRight: '0.5rem',
    },
  },
})

const ACCOUNT_FILTER_DESCRIPTIONS = {
  all: 'All accounts connected to your Merklebase workspace',
  mapped: 'Accounts currently mapped to this portfolio',
  available: "Available accounts that aren't assigned to any portfolio yet",
  mapped_elsewhere: (
    <>
      Accounts assigned to other portfolios.
      <br />
      Mapping them here will prevent those portfolios from being displayed together in filters.
    </>
  ),
}

export default function MapPortfolioAccountsDrawerContent({ portfolio = {}, onSave }) {
  const theme = useTheme()
  const styles = createStyles(theme)
  const { openSnackbar } = useSnackbar()
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [conflictingIdsByAgreementId, setConflictingIdsByAgreementId] = useState({})

  const memoizedPortfolio = useMemo(() => cloneDeep(portfolio), [portfolio])

  const [draftPortfolio, setDraftPortfolio] = useState(memoizedPortfolio)

  const addAccountsToPortfolio = useCallback(
    ({ accountsIds }) => {
      const finalAccountsIds = Array.from(
        new Set([...(draftPortfolio?.accountsIds ?? []), ...accountsIds])
      )

      draftPortfolio.accountsIds = finalAccountsIds
      setDraftPortfolio({ ...draftPortfolio })
    },
    [draftPortfolio]
  )

  const removeAccountsFromPortfolio = useCallback(
    ({ accountsIds }) => {
      const finalAccountsIds = draftPortfolio?.accountsIds.filter(
        (accountId) => !accountsIds.includes(accountId)
      )

      draftPortfolio.accountsIds = finalAccountsIds
      setDraftPortfolio({ ...draftPortfolio })
    },
    [draftPortfolio]
  )

  const addPortfolioToPortfolio = useCallback(
    ({ portfoliosIds }) => {
      const finalPortfoliosIds = Array.from(
        new Set([...(draftPortfolio?.portfolioIds ?? []), ...portfoliosIds])
      )

      draftPortfolio.portfolioIds = finalPortfoliosIds
      setDraftPortfolio({ ...draftPortfolio })
    },
    [draftPortfolio]
  )

  const removePortfolioFromPortfolio = useCallback(
    ({ portfoliosIds }) => {
      const finalPortfoliosIds = draftPortfolio?.portfolioIds.filter(
        (portfolioId) => !portfoliosIds.includes(portfolioId)
      )

      draftPortfolio.portfolioIds = finalPortfoliosIds
      setDraftPortfolio({ ...draftPortfolio })
    },
    [draftPortfolio]
  )

  const [searchValue, setSearchValue] = useState('')
  const isDisabled = useMemo(
    () => ({
      portfolios: draftPortfolio?.accountsIds?.length > 0,
      accounts: draftPortfolio?.portfolioIds?.length > 0,
    }),
    [draftPortfolio?.accountsIds?.length, draftPortfolio?.portfolioIds?.length]
  )
  const [portfolioType, setPortfolioType] = useState(
    !isDisabled?.accounts ? 'accounts' : 'portfolios'
  )

  const {
    portfolios: { data: selectedPortfolios },
    replaceAccountsInPortfolio: {
      mutation: replaceAccountsInPortfolio,
      isLoading: isReplaceAccountsInPortfolioLoading,
    },
    replacePortfoliosInPortfolio: {
      mutation: replacePortfoliosInPortfolio,
      isLoading: isReplacePortfoliosInPortfolioLoading,
    },
  } = usePortfolios()

  const { getAccountsByProvider } = useAccounts({ ignorePortfolios: true })

  const [accountFilter, setAccountFilter] = useState('all')

  const accountsByProvider = useMemo(
    () => getAccountsByProvider(searchValue),
    [getAccountsByProvider, searchValue]
  )

  const portfolioFiltered = useMemo(
    () =>
      selectedPortfolios?.filter((p) =>
        p?.name?.toLowerCase()?.includes(searchValue.toLowerCase())
      ),
    [searchValue, selectedPortfolios]
  )

  const onSearchInputChange = useCallback((event) => {
    const value = event.target.value
    setSearchValue(value)
  }, [])

  const isMapButtonDisabled = useMemo(() => {
    const field = portfolioType === 'accounts' ? 'accountsIds' : 'portfolioIds'

    if (portfolio.isCounterparty !== draftPortfolio.isCounterparty) return false
    if (portfolio[field]?.length !== draftPortfolio[field]?.length) return false
    return portfolio[field]?.every((id, index) => id === draftPortfolio[field][index])
  }, [draftPortfolio, portfolio, portfolioType])

  const onMapClick = useCallback(async () => {
    try {
      const replaceAccountsOrPortfoliosPromise =
        portfolioType === 'accounts'
          ? replaceAccountsInPortfolio({
              accountsIds: draftPortfolio?.accountsIds,
              portfolioId: draftPortfolio?.id,
            })
          : replacePortfoliosInPortfolio({
              portfoliosIds: draftPortfolio?.portfolioIds,
              portfolioId: draftPortfolio?.id,
            })

      const result = await replaceAccountsOrPortfoliosPromise
      if (result.error) {
        throw result.error
      }

      openSnackbar({
        message: `Portfolio mappings successfully`,
        color: 'success',
      })
      onSave()
    } catch (error) {
      if (error?.status === 400 && error?.data?.conflictingIds) {
        const mappedIds =
          error?.data?.conflictingIds?.reduce((acc, x) => {
            if (!acc[x.agreement_id]) {
              acc[x.agreement_id] = {
                name: x.agreement_name,
                ids: [],
              }
            }
            acc[x.agreement_id].ids.push(x)
            return acc
          }, {}) ?? {}
        setConflictingIdsByAgreementId(mappedIds)
        setIsErrorModalOpen(true)
      } else {
        openSnackbar({
          message: `Failed to save portfolio mappings, please try again or contact support`,
          color: 'error',
        })
      }
    }
  }, [
    draftPortfolio,
    onSave,
    openSnackbar,
    portfolioType,
    replaceAccountsInPortfolio,
    replacePortfoliosInPortfolio,
  ])

  const isMapButtonLoading = useMemo(() => {
    return isReplaceAccountsInPortfolioLoading || isReplacePortfoliosInPortfolioLoading
  }, [isReplaceAccountsInPortfolioLoading, isReplacePortfoliosInPortfolioLoading])

  const handleErrorModalClose = useCallback(() => {
    setIsErrorModalOpen(false)
  }, [])

  const handleReset = useCallback(() => {
    setDraftPortfolio(cloneDeep(portfolio))
  }, [portfolio])

  const {
    accountsById: { data: accountsById },
  } = useAccounts()

  return (
    <>
      <Grid container style={styles.container}>
        <Grid
          item
          xs={12}
          style={styles.form}
          container
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Grid item container xs={12} style={styles.titleContainer}>
            <Grid item xs={6}>
              <Label text="Portfolio Name">
                <Typography variant="h4" sx={{ pt: 0 }}>
                  {draftPortfolio?.name}
                </Typography>
              </Label>
            </Grid>
            <Grid item xs={6}>
              <Label
                text="Hierarchy type"
                tooltip={
                  <>
                    Portfolios can be organized by accounts or by <br /> other portfolios. <br />
                    Once set, this cannot be changed until all mapped <br /> items are removed from
                    this portfolio.
                  </>
                }
              >
                <Select
                  fullWidth
                  value={portfolioType}
                  onChange={(event) => setPortfolioType(event.target.value)}
                >
                  <MenuItem value="accounts">Account</MenuItem>
                  <MenuItem value="portfolios">Portfolio</MenuItem>
                </Select>
              </Label>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <OutlinedInput
              fullWidth
              size="small"
              startAdornment={<SearchOutlined />}
              placeholder={
                portfolioType === 'accounts'
                  ? 'Search Account or Provider..'
                  : 'Search Portfolio Name'
              }
              sx={styles.search}
              onChange={onSearchInputChange}
            />
          </Grid>
          {portfolioType === 'accounts' && (
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={accountFilter}
                exclusive
                sx={{ width: '100%', marginTop: theme.spacing(2), marginBottom: theme.spacing(1) }}
                onChange={(_, value) => value != null && setAccountFilter(value)}
              >
                <ToggleButton value="all" sx={{ width: '20%' }}>
                  All
                </ToggleButton>
                <ToggleButton value="mapped" sx={{ width: '25%' }}>
                  Mapped ({draftPortfolio?.accountsIds?.length ?? 0})
                </ToggleButton>
                <ToggleButton value="available" sx={{ width: '20%' }}>
                  Available
                </ToggleButton>
                <ToggleButton value="mapped_elsewhere" sx={{ width: '35%' }}>
                  Mapped Elsewhere
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {ACCOUNT_FILTER_DESCRIPTIONS[accountFilter]}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sx={styles.mappedContainer}>
            {portfolioType === 'accounts' ? (
              <Grid
                sx={styles.itemsContainer}
                container
                gap={1}
                flexDirection="column"
                flexWrap="nowrap"
              >
                {Object.keys(accountsByProvider)?.map((provider) => {
                  const providerAccounts = accountsByProvider[provider]

                  const unmappedOnly = accountFilter === 'available'
                  if (unmappedOnly) {
                    const areAccountAndSubAccountsFullyMapped = (accountId) => {
                      const accountData = accountsById[accountId]

                      if (!accountData) return false

                      const subAccounts = accountData.subAccounts || []

                      if (subAccounts.length === 0) {
                        return !accountData.unmapped
                      }

                      return (
                        !accountData.unmapped &&
                        accountData.subAccounts.every((subAccount) => {
                          return areAccountAndSubAccountsFullyMapped(subAccount.id)
                        })
                      )
                    }
                    const allAccountsMapped = Object.values(providerAccounts).every(
                      ({ account }) => {
                        return areAccountAndSubAccountsFullyMapped(account.id)
                      }
                    )

                    if (allAccountsMapped) return null
                  }

                  if (accountFilter === 'mapped') {
                    const hasMappedAccount = Object.values(providerAccounts).some(({ account }) =>
                      draftPortfolio?.accountsIds?.includes(account.id)
                    )
                    if (!hasMappedAccount) return null
                  }

                  if (accountFilter === 'mapped_elsewhere') {
                    const hasMappedElsewhereAccount = Object.values(providerAccounts).some(
                      ({ account }) => {
                        const data = accountsById[account.id]
                        return (
                          data &&
                          !data.unmapped &&
                          !draftPortfolio?.accountsIds?.includes(account.id)
                        )
                      }
                    )
                    if (!hasMappedElsewhereAccount) return null
                  }

                  return (
                    <Grid item xs={12} key={provider} sx={styles.portfolioItem}>
                      <AccountsByProvider
                        portfolio={draftPortfolio}
                        accountsById={accountsById}
                        provider={provider}
                        accounts={providerAccounts}
                        unmappedOnly={unmappedOnly}
                        accountFilter={accountFilter}
                        addAccountsToPortfolio={addAccountsToPortfolio}
                        removeAccountsFromPortfolio={removeAccountsFromPortfolio}
                        isCollapsed={searchValue}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                sx={styles.itemsContainer}
                container
                gap={1}
                flexDirection="column"
                flexWrap="nowrap"
              >
                {portfolioFiltered
                  ?.filter((p) => p.id !== draftPortfolio?.id)
                  ?.map((p) => {
                    return (
                      <Grid item xs={12} key={p.id} sx={styles.portfolioItem}>
                        <PortfoliosSelect
                          parentPortfolio={draftPortfolio}
                          portfolio={p}
                          isPortfolioInPortfolio={draftPortfolio?.portfolioIds.includes(p.id)}
                          name={p.name}
                          addPortfolioToPortfolio={addPortfolioToPortfolio}
                          removePortfolioFromPortfolio={removePortfolioFromPortfolio}
                        />
                      </Grid>
                    )
                  })}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          container
          rowSpacing={0.5}
          columnSpacing={1}
          mt={1.5}
          sx={styles.buttonContainer}
        >
          <Grid item xs={6}>
            <Button fullWidth variant="outlined" color="secondary" onClick={handleReset}>
              Reset
            </Button>
          </Grid>
          <Grid item xs={6}>
            <LoadingButton
              variant="contained"
              color="primary"
              sx={styles.button}
              fullWidth
              disabled={isMapButtonDisabled}
              onClick={onMapClick}
              loading={isMapButtonLoading}
            >
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
      <Dialog open={isErrorModalOpen} onClose={handleErrorModalClose} maxWidth="sm">
        <DialogTitle>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>Unable to Remove Accounts from Portfolio</Grid>
            <Grid item>
              <IconButton color="secondary" onClick={handleErrorModalClose}>
                <CloseOutlined />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Stack direction={'row'} alignItems={'baseline'} gap={1}>
            <Typography variant="h6" color={theme.palette.info.light}>
              <ExclamationCircleOutlined />
            </Typography>
            <Stack spacing={4} paddingTop={3}>
              <Typography variant="h6">
                The following accounts cannot be removed from the portfolio as it is linked to one
                or more Credit Agreements.
              </Typography>

              {Object.keys(conflictingIdsByAgreementId).map((agreementId) => {
                return (
                  <Box sx={styles.errorListBox}>
                    <Typography variant="h6">Account/s:</Typography>
                    <List>
                      {conflictingIdsByAgreementId[agreementId].ids.map((idMetadata) => {
                        return (
                          <ListItem>
                            <Typography key={idMetadata.account_id} variant="h6">
                              {idMetadata.account_name}
                            </Typography>
                          </ListItem>
                        )
                      })}
                    </List>
                    <Typography variant="h6">
                      Are linked to the following credit agreement:
                    </Typography>
                    <List>
                      <ListItem>
                        <Typography variant="h6">
                          {conflictingIdsByAgreementId[agreementId].name}
                        </Typography>
                      </ListItem>
                    </List>
                  </Box>
                )
              })}
              <Typography variant="h6">
                To proceed, please update the credit agreement before attempting again.
              </Typography>
              <Stack alignItems={'center'} columnSpacing={1.5}>
                <Button
                  onClick={handleErrorModalClose}
                  variant="contained"
                  sx={styles.errorBackButton}
                >
                  Back
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
